import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarIcon,
  ClockIcon,
  ExternalLinkIcon,
  MapPinIcon,
  UsersRoundIcon,
} from 'lucide-react';
import type { AgendaEvent, IntervisieGroupView, SmoelenboekEntry } from '@kennisbank/shared';
import { Layout, OnlineDot, useOnlineUserIds } from '../components/Layout.js';
import { AgendaRegistrations } from '../components/AgendaRegistrations.js';
import { RichText } from '../components/RichText.js';
import { apiGet } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';
import { initials } from '../lib/format.js';
import { MONTH_ABBR, isUpcoming, langeDatum, parseDate } from '../lib/datum.js';
import { teamsHulptekst } from '../lib/teams.js';

/**
 * Groepspagina, niet groepenlijst: vrijwel iedereen zit in precies één
 * intervisiegroep (besluit 3 september 2026), dus de pagina ís die groep.
 * Bij meer groepen komt er een schakelaar boven; de rest blijft gelijk.
 *
 * Geen eigen data: leden komen uit het smoelenboek en sessies uit de agenda
 * (type `intervisie`, al door de backend op lidmaatschap gefilterd). De
 * Teams-link staat op de groep en komt via de agenda ook op elke sessie.
 */

export function IntervisiePage(): JSX.Element {
  const [groups, setGroups] = useState<IntervisieGroupView[] | null>(null);
  const [users, setUsers] = useState<SmoelenboekEntry[]>([]);
  const [events, setEvents] = useState<AgendaEvent[]>([]);
  const [loadError, setLoadError] = useState(false);
  const [gekozenId, setGekozenId] = useState<string | null>(null);

  useEffect(() => {
    // Alleen de groep zelf is verplicht; zonder smoelenboek of agenda blijft de
    // pagina bruikbaar met een lege leden- of sessielijst.
    Promise.all([
      apiGet<{ groups: IntervisieGroupView[] }>('/api/v1/intervisie/groups'),
      apiGet<{ users: SmoelenboekEntry[] }>('/api/v1/users/smoelenboek').catch(() => ({
        users: [],
      })),
      apiGet<{ events: AgendaEvent[] }>('/api/v1/agenda').catch(() => ({ events: [] })),
    ])
      .then(([g, u, e]) => {
        setGroups(g.groups);
        setUsers(u.users);
        setEvents(e.events);
      })
      .catch(() => setLoadError(true));
  }, []);

  const group = groups?.find((g) => g.id === gekozenId) ?? groups?.[0];

  return (
    <Layout>
      <div className="h-full overflow-y-auto">
        <div className="mx-auto max-w-7xl p-6">
          {loadError && (
            <p className="text-[13px] text-error">
              Kon je intervisiegroep niet laden. Probeer de pagina te verversen.
            </p>
          )}
          {!loadError && groups === null && <p className="text-[13px] text-ink-900/50">Laden…</p>}
          {groups !== null && !group && <GeenGroep />}
          {groups !== null && group && (
            <>
              {groups.length > 1 && (
                <div className="mb-4 flex flex-wrap gap-2">
                  {groups.map((g) => (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => setGekozenId(g.id)}
                      className={`rounded-full px-3.5 py-1.5 text-[13px] font-semibold transition-colors ${
                        g.id === group.id
                          ? 'bg-brand-600 text-white'
                          : 'bg-white text-ink-900/70 ring-1 ring-brand-100 hover:bg-brand-50'
                      }`}
                    >
                      {g.name}
                    </button>
                  ))}
                </div>
              )}
              <Groep
                group={group}
                // Alleen leden met een smoelenboek-vermelding: wie gedeactiveerd
                // is (offboarding is handmatig) valt hier stil weg, dus de telling
                // kan lager zijn dan in Beheer Intervisie.
                leden={group.ledenUserIds
                  .map((id) => users.find((u) => u.id === id))
                  .filter((u): u is SmoelenboekEntry => u !== undefined)
                  .sort((a, b) => a.displayName.localeCompare(b.displayName, 'nl'))}
                sessies={events.filter(
                  (e) => e.evenementtype === 'intervisie' && e.intervisieGroupId === group.id,
                )}
              />
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}

function GeenGroep(): JSX.Element {
  const isAdmin = useAuth().user?.role === 'suppohrt_admin';
  return (
    <div className="flex flex-col items-center gap-3 rounded-2xl border border-brand-100 bg-white px-6 py-16 text-center">
      <UsersRoundIcon size={32} className="text-ink-900/20" />
      <p className="max-w-sm text-[13px] text-ink-900/55">
        {isAdmin
          ? 'Je bent zelf geen lid van een intervisiegroep. Groepen en leden beheer je onder Beheer Intervisie.'
          : 'Je bent nog niet aan een intervisiegroep gekoppeld. Neem contact op met je beheerder om aan een groep te worden toegevoegd.'}
      </p>
      {isAdmin && (
        <Link
          to="/beheer/intervisie"
          className="text-[13px] font-semibold text-brand-600 hover:text-brand-700"
        >
          Naar Beheer Intervisie
        </Link>
      )}
    </div>
  );
}

function Groep({
  group,
  leden,
  sessies,
}: {
  group: IntervisieGroupView;
  leden: SmoelenboekEntry[];
  sessies: AgendaEvent[];
}): JSX.Element {
  const komend = sessies.filter((e) => isUpcoming(e.date));
  const geweest = sessies.filter((e) => !isUpcoming(e.date)).reverse();
  const volgende = komend[0];
  // Elke datum kan een eigen vergadering hebben (besluit 2 nov 2026); de
  // knop bovenaan moet naar de eerstvolgende, niet naar een oude groepslink.
  const teamsLink = volgende?.teamsUrl ?? group.teamsUrl;

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-4 rounded-2xl border border-brand-100 bg-white p-6 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-brand-600">
            Je intervisiegroep
          </p>
          <h1 className="mt-1 text-2xl font-bold text-ink-900">{group.name}</h1>
          {(group.voorkeursdag || group.standaardTijd) && (
            <p className="mt-1 text-[13px] font-medium text-ink-900/70">
              {group.voorkeursdag ? `Meestal op ${group.voorkeursdag}` : 'Wisselende dag'}
              {group.standaardTijd ? `, ${group.standaardTijd}` : ''}
            </p>
          )}
          {group.description && (
            <p className="mt-2 max-w-2xl text-[13px] leading-relaxed text-ink-900/60">
              {group.description}
            </p>
          )}
        </div>
        {teamsLink ? (
          <div className="flex shrink-0 flex-col items-start gap-1.5 sm:items-end">
            <a
              href={teamsLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-brand-600 px-5 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-brand-700"
            >
              Open in Teams
              <ExternalLinkIcon size={15} strokeWidth={2} />
            </a>
            <p className="max-w-xs text-[11px] text-ink-900/45 sm:text-right">
              {teamsHulptekst('je SUPPOHRT-account')}
            </p>
          </div>
        ) : (
          <span className="shrink-0 text-[13px] font-medium text-ink-900/45">Teams-link volgt</span>
        )}
      </header>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <section className="flex flex-col gap-4">
          <h2 className="flex items-center gap-2 text-[15px] font-bold text-ink-900">
            <CalendarIcon size={16} className="text-brand-600" />
            Sessies
          </h2>
          {volgende ? (
            <VolgendeSessie event={volgende} />
          ) : (
            <p className="rounded-2xl border border-dashed border-brand-100 px-5 py-8 text-center text-[13px] text-ink-900/50">
              Er staat nog geen sessie gepland.
            </p>
          )}
          {komend.length > 1 && <SessieLijst titel="Daarna" events={komend.slice(1)} />}
          {geweest.length > 0 && (
            <SessieLijst titel="Geweest" events={geweest.slice(0, 5)} gedempt />
          )}
        </section>

        <section className="flex flex-col gap-4">
          <h2 className="flex items-center gap-2 text-[15px] font-bold text-ink-900">
            <UsersRoundIcon size={16} className="text-brand-600" />
            Leden
            <span className="font-normal text-ink-900/50">({leden.length})</span>
          </h2>
          {leden.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-brand-100 px-5 py-8 text-center text-[13px] text-ink-900/50">
              Nog geen leden gekoppeld.
            </p>
          ) : (
            <ul className="divide-y divide-brand-100 rounded-2xl border border-brand-100 bg-white">
              {leden.map((lid) => (
                <Lid key={lid.id} entry={lid} />
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function VolgendeSessie({ event }: { event: AgendaEvent }): JSX.Element {
  const { day, month } = parseDate(event.date);
  return (
    <div className="overflow-hidden rounded-2xl border border-brand-100 bg-white">
      <div className="flex">
        <div className="flex w-24 shrink-0 flex-col items-center justify-center bg-brand-600 py-5">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-white/70">
            {MONTH_ABBR[month]}
          </span>
          <span className="text-4xl font-black leading-none text-white">{day}</span>
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1.5 px-5 py-4">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-brand-600">
            Volgende sessie
          </p>
          <h3 className="text-[16px] font-bold leading-snug text-ink-900">{event.title}</h3>
          <div className="flex items-center gap-1.5 text-[13px] text-ink-900/60">
            <ClockIcon size={12} className="shrink-0" />
            {langeDatum(event.date)}, {event.time}
          </div>
          {event.location && (
            <div className="flex items-center gap-1.5 text-[13px] text-ink-900/60">
              <MapPinIcon size={12} className="shrink-0" />
              {event.location}
            </div>
          )}
          {event.description && (
            <RichText
              value={event.description}
              className="mt-1 text-[13px] leading-relaxed text-ink-900/60"
            />
          )}
        </div>
      </div>
      {event.registrationsOpen && (
        <div className="border-t border-brand-100 px-5 py-4">
          <AgendaRegistrations event={event} />
        </div>
      )}
    </div>
  );
}

function SessieLijst({
  titel,
  events,
  gedempt = false,
}: {
  titel: string;
  events: AgendaEvent[];
  gedempt?: boolean;
}): JSX.Element {
  return (
    <div>
      <h3 className="mb-2 text-[12px] font-semibold uppercase tracking-wider text-ink-900/45">
        {titel}
      </h3>
      <ul
        className={`divide-y divide-brand-100 rounded-2xl border border-brand-100 bg-white ${gedempt ? 'opacity-70' : ''}`}
      >
        {events.map((e) => (
          <li key={e.id} className="flex items-center gap-4 px-5 py-3">
            <span className="w-40 shrink-0 text-[13px] text-ink-900/60">{langeDatum(e.date)}</span>
            <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-ink-900">
              {e.title}
            </span>
            <span className="shrink-0 text-[12px] text-ink-900/50">{e.time}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Lid({ entry }: { entry: SmoelenboekEntry }): JSX.Element {
  const [imgError, setImgError] = useState(false);
  const online = useOnlineUserIds().has(entry.id);
  const subtitel = entry.functietitel || entry.specialisaties?.slice(0, 3).join(' · ') || '';
  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <div className="relative shrink-0">
        {entry.photoUrl && !imgError ? (
          <img
            src={entry.photoUrl}
            alt={entry.displayName}
            onError={() => setImgError(true)}
            className="h-10 w-10 rounded-full object-cover ring-2 ring-brand-100"
          />
        ) : (
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-100 text-[13px] font-bold text-brand-700 ring-2 ring-brand-200">
            {initials(entry.displayName)}
          </div>
        )}
        {online && <OnlineDot />}
      </div>
      <div className="min-w-0">
        <p className="truncate text-[13px] font-semibold text-ink-900">{entry.displayName}</p>
        {subtitel && <p className="truncate text-[12px] text-ink-900/55">{subtitel}</p>}
      </div>
    </li>
  );
}
